import {
	type FieldKey as InvestmentMetricKey,
	PortfolioExAnteMetricTypeEnum,
	PortfolioMetricTypes,
} from "@eu.mdotm.api/sphere";

export type UnitOfMeasurement = "" | "%";

/**
 * Score classifications (the `Avg. …` metrics) arrive from the backend with no unit metadata, so we
 * infer it from the name: anything that reads as a "… rate" (e.g. "Avg. Coupon Rate") is a
 * percentage. Matches "rate" only as a whole word — case-insensitive — so "Coupon Rate" qualifies
 * but substrings like "accurate" do not.
 */
export function unitOfMeasurementForClassificationName(
	classificationName: string | null | undefined,
): UnitOfMeasurement {
	return classificationName != null && /\brate\b/i.test(classificationName) ? "%" : "";
}

/**
 * Display metadata for a score-classification metric (the `Avg. …` metrics). These arrive from the
 * backend with no unit and with rates expressed as fractions (0.025 = 2.5%), so we derive both from
 * the name: a whole-word "rate" is a percentage whose raw value must be scaled ×100 to match the
 * "%" label (the renderers only append the suffix, they don't rescale). Everything else is shown
 * unit-less and unscaled.
 */
export function classificationMetricDisplay(classificationName: string | null | undefined): {
	unitOfMeasurement: UnitOfMeasurement;
	scale: number;
} {
	const unitOfMeasurement = unitOfMeasurementForClassificationName(classificationName);
	return { unitOfMeasurement, scale: unitOfMeasurement === "%" ? 100 : 1 };
}

const portfolioList: Partial<Record<InvestmentMetricKey, UnitOfMeasurement>> = {
	PERFORMANCE_SINCE_INCEPTION: "%",
	PERFORMANCE_YTD: "%",
	PERFORMANCE_1Y: "%",
	PERFORMANCE_6M: "%",
	PERFORMANCE_3M: "%",
	PERFORMANCE_1M: "%",
	PERFORMANCE_MTD: "%",
	PERFORMANCE_1W: "%",
	PERFORMANCE_QTD: "%",
	PERFORMANCE_1D: "%",
	SORTINO_SINCE_INCEPTION: "",
	SORTINO_YTD: "",
	SORTINO_1Y: "",
	SORTINO_6M: "",
	SORTINO_3M: "",
	SORTINO_1M: "",
	SORTINO_MTD: "",
	SORTINO_1W: "",
	SORTINO_QTD: "",
	MAX_DRAWDOWN_SINCE_INCEPTION: "%",
	MAX_DRAWDOWN_YTD: "%",
	MAX_DRAWDOWN_MTD: "%",
	MAX_DRAWDOWN_1Y: "%",
	MAX_DRAWDOWN_6M: "%",
	MAX_DRAWDOWN_3M: "%",
	MAX_DRAWDOWN_1M: "%",
	MAX_DRAWDOWN_1W: "%",
	MAX_DRAWDOWN_QTD: "%",
	VOLATILITY_SINCE_INCEPTION: "%",
	VOLATILITY_YTD: "%",
	VOLATILITY_MTD: "%",
	VOLATILITY_1Y: "%",
	VOLATILITY_6M: "%",
	VOLATILITY_3M: "%",
	VOLATILITY_1M: "%",
	VOLATILITY_1W: "%",
	VOLATILITY_QTD: "%",
	EFFICIENCY_RATIO_SINCE_INCEPTION: "",
	EFFICIENCY_RATIO_YTD: "",
	EFFICIENCY_RATIO_MTD: "%",
	EFFICIENCY_RATIO_1Y: "",
	EFFICIENCY_RATIO_6M: "",
	EFFICIENCY_RATIO_3M: "",
	EFFICIENCY_RATIO_1M: "",
	EFFICIENCY_RATIO_1W: "",
	EFFICIENCY_RATIO_QTD: "",
	EX_ANTE_RETURN_3Y: "%",
	EX_ANTE_EFFICIENCY_RATIO_3Y: "",
	EX_ANTE_MAX_DRAWDOWN_3Y: "%",
	EX_ANTE_DIVERSIFICATION_RATIO_3Y: "%",
	TRACKING_ERROR_3Y: "%",
	PARAMETRIC_VAR_95_1Y: "%",
	PARAMETRIC_VAR_975_1Y: "%",
	PARAMETRIC_VAR_99_1Y: "%",
	PARAMETRIC_VAR_95_2Y: "%",
	PARAMETRIC_VAR_975_2Y: "%",
	PARAMETRIC_VAR_99_2Y: "%",
	PARAMETRIC_VAR_95_3Y: "%",
	PARAMETRIC_VAR_975_3Y: "%",
	PARAMETRIC_VAR_99_3Y: "%",
	HISTORICAL_VAR_95_1Y: "%",
	HISTORICAL_VAR_975_1Y: "%",
	HISTORICAL_VAR_99_1Y: "%",
	HISTORICAL_VAR_95_2Y: "%",
	HISTORICAL_VAR_975_2Y: "%",
	HISTORICAL_VAR_99_2Y: "%",
	HISTORICAL_VAR_95_3Y: "%",
	HISTORICAL_VAR_975_3Y: "%",
	HISTORICAL_VAR_99_3Y: "%",
	EX_ANTE_VOLATILITY_1Y: "%",
	EX_ANTE_VOLATILITY_2Y: "%",
	EX_ANTE_VOLATILITY_3Y: "%",
	EX_ANTE_VOLATILITY_6M: "%",
	EXPOSURE_MACRO_EQUITY: "%",
	EXPOSURE_MACRO_FIXED_INCOME: "%",
	EXPOSURE_MACRO_COMMODITIES: "%",
	EXPOSURE_MACRO_ALTERNATIVE: "%",
	EXPOSURE_MACRO_MONEY_MARKET: "%",

	SHARPE_RATIO_SINCE_INCEPTION: "",
	SHARPE_RATIO_YTD: "",
	SHARPE_RATIO_MTD: "",
	SHARPE_RATIO_1Y: "",
	SHARPE_RATIO_6M: "",
	SHARPE_RATIO_3M: "",
	SHARPE_RATIO_1M: "",
	SHARPE_RATIO_1W: "",
	SHARPE_RATIO_QTD: "",
	CALMAR_RATIO_SINCE_INCEPTION: "",
	CALMAR_RATIO_YTD: "",
	CALMAR_RATIO_MTD: "",
	CALMAR_RATIO_1Y: "",
	CALMAR_RATIO_6M: "",
	CALMAR_RATIO_3M: "",
	CALMAR_RATIO_1M: "",
	CALMAR_RATIO_1W: "",
	CALMAR_RATIO_QTD: "",
	ALPHA_SINCE_INCEPTION: "%",
	ALPHA_YTD: "%",
	ALPHA_MTD: "%",
	ALPHA_1Y: "%",
	ALPHA_6M: "%",
	ALPHA_3M: "%",
	ALPHA_1M: "%",
	ALPHA_QTD: "%",
	BETA_SINCE_INCEPTION: "",
	BETA_YTD: "",
	BETA_MTD: "",
	BETA_1Y: "",
	BETA_6M: "",
	BETA_3M: "",
	BETA_1M: "",
	BETA_QTD: "",
	BATTING_AVERAGE_SINCE_INCEPTION: "%",
	BATTING_AVERAGE_MTD: "%",
	BATTING_AVERAGE_YTD: "%",
	BATTING_AVERAGE_1Y: "%",
	BATTING_AVERAGE_6M: "%",
	BATTING_AVERAGE_3M: "%",
	BATTING_AVERAGE_1M: "%",
	BATTING_AVERAGE_QTD: "%",
	UPSIDE_CAPTURE_RATIO_SINCE_INCEPTION: "%",
	UPSIDE_CAPTURE_RATIO_MTD: "%",
	UPSIDE_CAPTURE_RATIO_YTD: "%",
	UPSIDE_CAPTURE_RATIO_1Y: "%",
	UPSIDE_CAPTURE_RATIO_6M: "%",
	UPSIDE_CAPTURE_RATIO_3M: "%",
	UPSIDE_CAPTURE_RATIO_1M: "%",
	UPSIDE_CAPTURE_RATIO_QTD: "%",
	DOWNSIDE_CAPTURE_RATIO_SINCE_INCEPTION: "%",
	DOWNSIDE_CAPTURE_RATIO_MTD: "%",
	DOWNSIDE_CAPTURE_RATIO_YTD: "%",
	DOWNSIDE_CAPTURE_RATIO_1Y: "%",
	DOWNSIDE_CAPTURE_RATIO_6M: "%",
	DOWNSIDE_CAPTURE_RATIO_3M: "%",
	DOWNSIDE_CAPTURE_RATIO_1M: "%",
	DOWNSIDE_CAPTURE_RATIO_QTD: "%",
	CORRELATION_SINCE_INCEPTION: "",
	CORRELATION_YTD: "",
	CORRELATION_MTD: "",
	CORRELATION_1Y: "",
	CORRELATION_6M: "",
	CORRELATION_3M: "",
	CORRELATION_1M: "",
	CORRELATION_QTD: "%",

	MAX_DRAWDOWN_TIME_TO_RECOVERY_1Y: "",
	MAX_DRAWDOWN_TIME_TO_RECOVERY_SINCE_INCEPTION: "",
	MAX_DRAWDOWN_TIME_TO_RECOVERY_YTD: "",
	MAX_DRAWDOWN_TIME_TO_RECOVERY_MTD: "",
	MAX_DRAWDOWN_TIME_TO_RECOVERY_6M: "",
	MAX_DRAWDOWN_TIME_TO_RECOVERY_3M: "",
	MAX_DRAWDOWN_TIME_TO_RECOVERY_1M: "",
	MAX_DRAWDOWN_TIME_TO_RECOVERY_1W: "",
	MAX_DRAWDOWN_TIME_TO_RECOVERY_QTD: "",
	SCORE_AVERAGE: "",
	SCORE_AVERAGE_NAN_AS_ZEROS: "",
	BENCHMARK_EFFICIENCY_RATIO_1M: "",
	BENCHMARK_MAX_DRAWDOWN_1M: "%",
	BENCHMARK_PERFORMANCE_1M: "%",
	BENCHMARK_SORTINO_1M: "%",
	BENCHMARK_VOLATILITY_1M: "%",
	TAG_EXPOSURE_OPTION: "%",
	RISK_MODEL_EXPOSURE_AC: "%",
	TURNOVER: "%",
	MV_DISTANCE: "%",

	N_OF_BUY: "",
	N_OF_BUY_SELL: "",
	N_OF_INSTRUMENTS: "",
	N_OF_SELL: "",
};

const portfolioMetric = defineMetricSubset(Object.values(PortfolioMetricTypes));
const exAnteMetric = defineMetricSubset(Object.values(PortfolioExAnteMetricTypeEnum), {
	DIVERSIFICATION_RATIO_3Y: "%",
	EFFICIENCY_RATIO_3Y: "",
	RETURN_3Y: "%",
	MAX_DRAWDOWN_3Y: "%",
	VOLATILITY_2Y: "%",
	VOLATILITY_3Y: "%",
});

export const unitOfMeasurement = {
	portfolioList,
	portfolioMetric,
	exAnteMetric,
};

export const decimalPlaces = {
	exAnteMetric: {
		N_OF_INSTRUMENTS: 0,
		N_OF_BUY_SELL: 0,
		N_OF_BUY: 0,
		N_OF_SELL: 0,
	} as Partial<Record<PortfolioExAnteMetricTypeEnum, 0 | 1 | 2>>,
};

// Keys you requested that DON'T exist in portfolioList → must be supplied manually
type ManualRequired<Keys extends readonly string[]> = {
	[K in Exclude<Keys[number], InvestmentMetricKey>]: UnitOfMeasurement;
};

// ─── defineMetricSubset ───────────────────────────────────────────────────────
//
// Overload 1: all keys exist in portfolioList → manual arg is forbidden
// Overload 2: some keys are missing          → manual arg is required and
//             must cover exactly the missing keys

function defineMetricSubset<const Keys extends readonly InvestmentMetricKey[]>(
	keys: Keys,
): { [K in Keys[number]]: (typeof portfolioList)[K] };

function defineMetricSubset<const Keys extends readonly string[], const Manual extends ManualRequired<Keys>>(
	keys: Keys,
	manual: Manual,
): { [K in Extract<Keys[number], InvestmentMetricKey>]: (typeof portfolioList)[K] } & Manual;

function defineMetricSubset(keys: readonly string[], manual: Record<string, UnitOfMeasurement> = {}) {
	const fromList = Object.fromEntries(
		keys.filter((k): k is InvestmentMetricKey => k in portfolioList).map((k) => [k, portfolioList[k]]),
	);
	return { ...fromList, ...manual };
}
